import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DebugTools } from '@modules/Debug';
import { PaymentSocket } from '@services/pay';
import { Message, PageLoading } from '@wegic/ui';
import '@wegic/ui/dist/components/Message/index.css';
import '@wegic/ui/dist/components/PageLoading/index.css';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { matchRoutes, RouterProvider } from 'react-router-dom';
import styles from './app.module.less';
import { INVITE_CODE } from './components/CreditsModal/const';
import FeedbackModalLoadable from './components/FeedbackModal';
import GlobalCreditPurchaseModal from './components/GlobalCreditPurchaseModal';
import GlobalTrialPayModal from './components/GlobalTrialPayModal';
import LoginModal from './components/LoginModal';
import MobileGuide from './components/MobileGuide';
import { handleSubscriptionAndPointsAPI } from './components/PayModal/handle';
import ReferralModalLoadable from './components/ReferralModal';
import RemixUpgradeModal from './components/RemixUpgradeModal';
import { DEFAULT_PATH_AFTER_LOGIN } from './const';
import { isMobile } from './const/browser';
import { USER_NOT_LOGIN_ACCOUNT } from './const/code';
import { initAnonymousUser } from './helpers/anonymousUser';
import { useUserRefresh } from './helpers/hooks/useUserRefresh';
import { initClientFeatures } from './helpers/initClientFeatures';
import { initDiscountActivity } from './helpers/initDiscountActivity';
import { initProductList } from './helpers/initProductList';
import { useNotifyInit } from './helpers/notify';
import { useRemixUpgradeTrigger } from './helpers/useRemixUpgradeTrigger';
import { refreshAnonymousCredits } from './helpers/visitorCredits';
import I18nProvider from './i18n/Provider';
import { appLifecycle } from './modules/AppLifecycle';
import { setAnonymousCollectUserId, setCollectUserId } from './modules/Collect';
import CustomRouter from './modules/CustomRouter';
import { buildLoginRedirectPath, getLoginRedirect, hasRequireLoginRoute } from './modules/CustomRouter/helper';
import { customRoutes } from './modules/CustomRouter/routes';
import posthogModule from './modules/Posthog';
import { useInitialWebsiteGenerationSystemNotification } from './modules/SystemNotification';
import NotificationSocket from './services/socket/NotificationSocket';
import { getUserInfoRequest } from './services/user';
import { useRemixUpgradeModalStore } from './stores/remixUpgradeModal';
import { useUserStore } from './stores/user';
appLifecycle.beforeAppRender();
export function AppRoot() {
    const [isInitializing, setIsInitializing] = useState(true);
    const setUserInfo = useUserStore((state)=>state.setUserInfo);
    const setIsLogin = useUserStore((state)=>state.setIsLogin);
    // 跨路由监听已绑定项目的首次生成完成事件。
    useInitialWebsiteGenerationSystemNotification();
    // 初始化用户通知
    useNotifyInit();
    useUserRefresh(!isInitializing);
    // Remix 积分耗尽自动弹窗
    useRemixUpgradeTrigger();
    useEffect(()=>{
        appLifecycle.afterAppMount();
    }, []);
    // 拉取当前生效的折扣促销活动（全局，与登录态无关）
    useEffect(()=>{
        initDiscountActivity();
    }, []);
    useEffect(()=>{
        const initAnonymousContext = async ()=>{
            const anonymousUserId = await initAnonymousUser();
            setAnonymousCollectUserId(anonymousUserId);
            posthogModule.initializeAnonymousUser(anonymousUserId);
            if (anonymousUserId) {
                try {
                    NotificationSocket.initialize(anonymousUserId);
                } catch (error) {
                    console.warn('initialize anonymous notification socket failed:', error);
                }
                await refreshAnonymousCredits();
            }
        };
        const initAuth = async ()=>{
            const { pathname } = window.location;
            const isMaintenance = pathname.includes('/maintenance');
            if (isMaintenance) {
                setIsInitializing(false);
                return;
            }
            try {
                const response = await getUserInfoRequest({
                    allowedCodes: [
                        USER_NOT_LOGIN_ACCOUNT
                    ],
                    hideCodeErrorMessage: true,
                    hideHTTPErrorMessage: true
                });
                if (response === null || response === void 0 ? void 0 : response.data) {
                    const { userId } = response.data;
                    setUserInfo(response.data);
                    setIsLogin(true);
                    if (userId) {
                        PaymentSocket.initialize(userId);
                        setCollectUserId(userId);
                    }
                    await Promise.all([
                        handleSubscriptionAndPointsAPI()
                    ]);
                    posthogModule.identifyCurrentUser();
                    initProductList();
                } else {
                    setUserInfo(null);
                    setIsLogin(false);
                    useRemixUpgradeModalStore.getState().resetCreditPurchaseQuota();
                    posthogModule.reset();
                    await initAnonymousContext();
                }
            } catch (error) {
                console.error(error);
                setUserInfo(null);
                setIsLogin(false);
                useRemixUpgradeModalStore.getState().resetCreditPurchaseQuota();
                posthogModule.reset();
                await initAnonymousContext();
            }
            // 用户或匿名身份初始化完成后再读取 AB，避免首屏先渲染 control 再切 treatment。
            await initClientFeatures();
            if (pathname === '/login') {
                setIsInitializing(false);
                return;
            }
            setIsInitializing(false);
        };
        initAuth();
    }, [
        setUserInfo,
        setIsLogin
    ]);
    // 初始化完成后，检查当前路由是否需要登录
    useEffect(()=>{
        if (isInitializing) {
            return;
        }
        let timerId = null;
        const handleRouteProtection = async ()=>{
            const { hash, pathname, search } = window.location;
            const { isLogin } = useUserStore.getState();
            const searchParams = new URLSearchParams(search);
            const matches = matchRoutes(customRoutes, pathname);
            if (!(matches === null || matches === void 0 ? void 0 : matches.length)) {
                return;
            }
            // 如果当前路由需要登录但用户未登录，重定向到登录页
            if (hasRequireLoginRoute(matches) && !isLogin) {
                CustomRouter.navigate(buildLoginRedirectPath(pathname, search, hash));
                return;
            }
            // 如果用户已登录但访问登录页，重定向到来源页或首页
            if (pathname === '/login' && isLogin) {
                const redirect = getLoginRedirect(searchParams) || DEFAULT_PATH_AFTER_LOGIN;
                CustomRouter.navigate(redirect, {
                    replace: true
                });
                const inviteCode = searchParams.get(INVITE_CODE);
                if (inviteCode) {
                    timerId = setTimeout(()=>{
                        Message.normal(intl.get('credits.tip.inviteForExistedUser'));
                    }, 1000);
                }
            }
        };
        handleRouteProtection();
        return ()=>{
            if (timerId) {
                clearTimeout(timerId);
            }
        };
    }, [
        isInitializing
    ]);
    return isInitializing ? /*#__PURE__*/ _jsx(PageLoading, {}) : /*#__PURE__*/ _jsx("div", {
        className: styles['app-wrapper'],
        children: /*#__PURE__*/ _jsxs(I18nProvider, {
            children: [
                /*#__PURE__*/ _jsx(MobileGuide, {}),
                /*#__PURE__*/ _jsx("div", {
                    className: classNames(styles.wrapper, {
                        [styles.mobile]: isMobile
                    }),
                    children: /*#__PURE__*/ _jsx(RouterProvider, {
                        router: CustomRouter
                    })
                }),
                /*#__PURE__*/ _jsx(DebugTools, {}),
                /*#__PURE__*/ _jsx(FeedbackModalLoadable, {}),
                /*#__PURE__*/ _jsx(ReferralModalLoadable, {}),
                /*#__PURE__*/ _jsx(GlobalCreditPurchaseModal, {}),
                /*#__PURE__*/ _jsx(GlobalTrialPayModal, {}),
                /*#__PURE__*/ _jsx(RemixUpgradeModal, {}),
                /*#__PURE__*/ _jsx(LoginModal, {})
            ]
        })
    });
}
